import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Image from 'next/image';
import React, { memo, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { postNewFolder } from '../../../apis/tms/folder.api';
import { updateFolder } from '../../../apis/tms/training.api';
import { handleCheckChangeForm } from '../../../common/checkForm';
import { handleResetForm, handleSubmitForm } from '../../../common/formCallApi';
import { renderSubmitFormContent } from '../../../common/formCommonFunc';
import { IModifyFolderModal } from '../../../interfaces/components/modal.interface';
import {
  FolderStatusEnum,
  IFolderInfo,
} from '../../../interfaces/training.interface';
import AuthContext from '../../../state/auth/AuthContext';
import { useModal } from '../../../state/modal-provider/ModalProvider';
import BottomDrawer from '../../elements/drawers/BottomDrawer';
import PrimaryInput from '../../input/PrimaryInput';
import PrimaryTextArea from '../../input/PrimaryTextArea';

const ModifyFolderModal: React.FC<IModifyFolderModal> = ({
  open,
  setOpen,
  defaultData,
  queryKey,
}) => {
  const queryClient = useQueryClient();
  const [folderInfo, setFolderInfo] = useState<IFolderInfo>({
    name: defaultData?.folderName ?? '',
    description: defaultData?.description ?? '',
    status: defaultData?.status ?? FolderStatusEnum.Public,
  });
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const handleSetState = (name: string, value: string) => {
    setFolderInfo((state) => ({ ...state, [name]: value }));
  };

  const [openToast, setOpenToast] = useState<{
    type: 'success' | 'error';
    content: string;
  }>({
    type: 'success',
    content: '',
  });

  const { toast: toastOld } = useModal();

  const toast = (content: string, type: 'success' | 'error' = 'success') => {
    setOpenToast({ type, content });
  };

  useEffect(() => {
    setFolderInfo({
      name: defaultData?.folderName ?? '',
      description: defaultData?.description ?? '',
      status: defaultData?.status ?? FolderStatusEnum.Public,
    });
  }, [defaultData]);

  const requiredKeys = ['name'];
  const { setLoading } = useContext(AuthContext);

  const resetForm = () =>
    handleResetForm({
      setValueState: setFolderInfo,
      initValueState: {
        name: defaultData?.name ?? '',
        description: defaultData?.description ?? '',
      },
    });

  const handleFormCancelCallback = () => {
    setOpen(false);
    resetForm();
    setIsSubmit(false);
  };

  const handleSubmit = () => {
    handleSubmitForm({
      callback: () => setIsSubmit(true),
      requiredKeys,
      data: {
        ...folderInfo,
        parentId: defaultData?.parentId || null,
        id: defaultData?.id || null,
      },
      id: defaultData?.id || '',
      asyncFunc: defaultData?.id ? updateFolder : postNewFolder,
      formCancelCallBack: handleFormCancelCallback,
      toggleSuccessModalNoti: () => {
        queryClient.refetchQueries(queryKey);
        toast(renderSubmitFormContent(''));
      },
      toggleErrorModalNoti: () => toast('Có lỗi xảy ra, vui lòng thử lại.'),
      setLoading,
    });
  };

  useEffect(() => {
    window &&
      window.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e?.key && e?.key === 'Escape') {
          handleToggleLeaveModal();
        }
      });

    return () => {
      window && window.addEventListener('keydown', () => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleLeaveModal = () => {
    if (
      !handleCheckChangeForm(folderInfo, {
        name: defaultData?.folderName ?? '',
        description: defaultData?.description ?? '',
      })
    ) {
      toastOld.warning({
        deleteTitle: 'Thoát',
        cancelTitle: 'Ở lại',
        onDelete: handleFormCancelCallback,
        title: 'Thay đổi chưa lưu',
        content:
          'Thay đổi của bạn chưa được lưu. Bạn vẫn muốn thoát mà không lưu?',
      });
    } else {
      setOpen(false);
    }
  };

  const useStyles = makeStyles({
    root: {
      zIndex: '900!important' as any,
    },
  });
  const classes = useStyles();

  return (
    <>
      {/* desktop modal */}
      <Dialog
        open={open}
        className={`${classes.root} hidden sm:block`}
        onClose={() => handleToggleLeaveModal()}
      >
        <div className="w-[400px] px-2">
          <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
              <p className="text-2xl font-bold">
                {defaultData ? 'Chỉnh sửa' : 'Tạo'} thư mục
              </p>
              <IconButton onClick={() => handleToggleLeaveModal()}>
                <Image
                  alt=""
                  src="/icons/close-icon.svg"
                  width={24}
                  height={24}
                />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <div className="mb-6">
              <p className="text mb-2 font-medium text-slate_gray">
                Tên thư mục *
              </p>
              <PrimaryInput
                desc="Tên thư mục"
                setValue={handleSetState}
                value={folderInfo.name}
                inputName="name"
                isObject
              />
              {isSubmit && !folderInfo.name && (
                <p className="text-sm text-red-400">
                  Vui lòng điền tên thư mục
                </p>
              )}
            </div>
            <div>
              <p className="text mb-2 font-medium text-slate_gray">
                Mô tả (không bắt buộc)
              </p>
              <PrimaryTextArea
                desc="Giới thiệu sơ về folder"
                setValue={handleSetState}
                value={folderInfo.description}
                inputName="description"
                isObject
              />
            </div>
            <div className="grid grid-cols-10 gap-2 py-5">
              <Button
                style={{ textTransform: 'none' }}
                className="col-span-2 w-full rounded-xl border-gray-300 py-3 text-black"
                variant="outlined"
                onClick={handleToggleLeaveModal}
              >
                Huỷ
              </Button>
              <Button
                style={{ textTransform: 'none' }}
                className="col-span-8 w-full rounded-xl border-gray-300 bg-primary_lighter py-3 text-white hover:bg-primary_lighter"
                variant="contained"
                onClick={handleSubmit}
              >
                {defaultData ? 'Lưu thay đổi' : 'Tạo thư mục'}
              </Button>
            </div>
          </DialogContent>
        </div>
      </Dialog>
      {/* mobile drawer */}
      <BottomDrawer
        isOpen={open}
        handleClose={() => handleToggleLeaveModal()}
        title={defaultData ? 'Chỉnh sửa nhóm' : 'Tạo nhóm'}
      >
        <DialogContent className="h-[90dvh]">
          <div className="mb-6">
            <p className="text mb-2 font-medium text-slate_gray">
              Tên thư mục *
            </p>
            <PrimaryInput
              desc="Tên thư mục"
              setValue={handleSetState}
              value={folderInfo.name}
              inputName="name"
              isObject
            />
            {isSubmit && !folderInfo.name && (
              <p className="text-sm text-red-400">Vui lòng điền tên thư mục</p>
            )}
          </div>
          <div>
            <p className="text mb-2 font-medium text-slate_gray">
              Mô tả (không bắt buộc)
            </p>
            <PrimaryTextArea
              desc="Giới thiệu sơ về folder"
              setValue={handleSetState}
              value={folderInfo.description}
              inputName="description"
              isObject
            />
          </div>
          <div className="flex flex-col gap-2 py-5">
            <Button
              style={{ textTransform: 'none' }}
              className="w-full rounded-xl border-gray-300 bg-primary_lighter py-3 text-white"
              variant="contained"
              onClick={handleSubmit}
            >
              {defaultData ? 'Lưu thay đổi' : 'Tạo thư mục'}
            </Button>
            <Button
              style={{ textTransform: 'none' }}
              className="w-full rounded-xl border-gray-300 py-3 text-black"
              variant="outlined"
              onClick={handleToggleLeaveModal}
            >
              Huỷ
            </Button>
          </div>
        </DialogContent>
      </BottomDrawer>
      <Snackbar
        autoHideDuration={1500}
        disableWindowBlurListener
        open={!!openToast.content}
        onClose={() => toast('')}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        className="rounded-md bg-black/75 p-4"
      >
        <div>
          {openToast.type === 'success' ? (
            <p className="text-white">{openToast.content}</p>
          ) : (
            <Alert severity="error">{openToast.content}</Alert>
          )}
        </div>
      </Snackbar>
    </>
  );
};

export default memo(ModifyFolderModal);
